import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';

/**
 * Comunicado expandido com campos avançados (paridade municipal).
 */
export interface ComunicadoEscola {
  id: string;
  titulo: string;
  corpo: string;
  tipo: string;
  categoria: string;
  destinatario: string;
  status: string;
  fixado: boolean;
  turmaId?: string;
  escolaId?: string;
  municipioId?: string;
  criadoPorId?: string;
  dataEvento?: string;
  agendadoPara?: string;
  templateId?: string;
  configPreview?: Record<string, string | boolean | number | null>;
  ativo: boolean;
  criadoEm: string;
  atualizadoEm: string;
  turma?: { id: string; nome: string } | null;
  escola?: { id: string; nome: string } | null;
  municipio?: { id: string; nome: string } | null;
  criadoPor?: { id: string; nome: string; perfil: string } | null;
  _count?: { visualizacoes: number };
}

export interface ComunicadoStats {
  total: number;
  enviados: number;
  agendados: number;
  rascunhos: number;
  totalVisualizacoes: number;
  comunicadosVisualizados: number;
  taxaAbertura: number;
  porCategoria: { categoria: string; total: number }[];
  porTipo: { tipo: string; total: number }[];
}

interface Filters {
  categoria?: string;
  status?: string;
}

/**
 * Hook dedicado para comunicados da escola.
 * Usa /escolas/:escolaId/comunicados com stats.
 */
export function useEscolaComunicados(filters?: Filters) {
  const queryClient = useQueryClient();
  const escolaId = useAuthStore((s) => s.escolaId);

  const basePath = escolaId ? `/escolas/${escolaId}/comunicados` : '';

  // Lista de comunicados
  const query = useQuery({
    queryKey: ['escola-comunicados', escolaId, filters],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters?.categoria) params.categoria = filters.categoria;
      if (filters?.status) params.status = filters.status;
      const { data } = await api.get(basePath, { params });
      return (Array.isArray(data) ? data : []) as ComunicadoEscola[];
    },
    enabled: !!escolaId,
  });

  // Stats/KPIs
  const statsQuery = useQuery({
    queryKey: ['escola-comunicados-stats', escolaId],
    queryFn: async () => {
      const { data } = await api.get(`${basePath}/stats`);
      return data as ComunicadoStats;
    },
    enabled: !!escolaId,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['escola-comunicados'] });
    queryClient.invalidateQueries({ queryKey: ['escola-comunicados-stats'] });
  };

  // Criar comunicado
  const create = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api.post(basePath, payload);
      return data;
    },
    onSuccess: invalidateAll,
  });

  // Atualizar comunicado
  const update = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Record<string, unknown>) => {
      const body = (payload && typeof (payload as any).data === 'object' && (payload as any).data !== null)
        ? { ...(payload as any).data, ...payload }
        : payload;
      delete (body as any).data;
      const { data } = await api.put(`${basePath}/${id}`, body);
      return data;
    },
    onSuccess: invalidateAll,
  });

  // Soft-delete
  const deactivate = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`${basePath}/${id}`);
      return data;
    },
    onSuccess: invalidateAll,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    stats: statsQuery.data ?? null,
    statsLoading: statsQuery.isLoading,
    create,
    update,
    deactivate,
  };
}
